/**
 * 月度执行面板（2026-10-04 全屏场景化重构 · 中央行程卡形态）
 *
 * 形态（原型 ui-concept/月度执行-中央行程卡版.html 定稿；该原型已于 2026-10-24 归档，现行实现见本文件）：
 *   - 全屏模态面板（场景背景=web/public/scenes/bg-*.webp 按行程类型切换，图未就位回退纸白），无导航栏；
 *   - 顶栏=决策日公共顶栏（createExecTopBar，1:1 同构；周进度点悬浮在顶栏正下方）；
 *   - 每周开始：中央行程卡（本周已安排行程）快闪 0.9s 自动收（点击可提前收）；
 *   - 底部常驻文案框：普通周=行程标签/行程名/类型+叙事+变动行；试镜周=入口短句+剧本册横排（点册框内切详情）；
 *   - 事件/偶遇统一走 VnDialog（事件为旁白态：无头像、姓名=事件标题；文案全用剧本库原文）；
 *   - 点屏幕任意处推进下一周（VN/册行/详情内点击不冒泡）；检定静态揭晓（骰子动效待后续批次补）。
 * 数据流与旧版一致：gameLoop.runWeek/finishMonth、EventSystem、encounterSystem、scriptSystem、5c 竞角揭晓页原样保留。
 */
import type { GameApp } from '../../app';
import { GameState } from '../../systems/GameState';
import { gameLoop, WeekResult, WeekChange } from '../../systems/GameLoop';
import { eventSystem } from '../../systems/EventSystem';
import { scriptSystem } from '../../systems/ScriptSystem';
import type { RevealResult } from '../../systems/ScriptSystem';
import { GameData } from '../../data/GameData';
import { fillAvatarSlot } from '../../avatar/gamemount';
import { SceneKey, SCENE_BGS, applyScenePlaceholder, clearScenePlaceholder, loadSceneBgImage, markSceneActive, scheduleScenePreheat } from '../../data/SceneBgs';
import { GameConfig } from '../../core/GameConfig';
import { Utils } from '../../core/Utils';
import { EventDef, ChoiceDef, ScriptDef, ScheduleSlot } from '../../data/types';
import { UNREACHABLE_LINES } from '../../data/tables/EncounterTexts';
import { encounterSystem, EncounterOffer, EncounterOutcome } from '../../systems/EncounterSystem';
import { FreeroamMap } from '../FreeroamMap';
import { freeroamSystem } from '../../systems/FreeroamSystem';
import { PlotPlayer } from '../plotPlayback';
import { PlotSystem } from '../../systems/PlotSystem';
import { isChatBeat } from '../plotStage';
import { roleCardHtml, bindPdFold } from '../roleCard';
import { ContractSignPop } from '../ContractSignPop';
import type { SignPopButton } from '../ContractSignPop';
import { DialogLine, DialogSpeaker, VnDialog, createDiceCube, DICE_LAND } from '../VnDialog';
import {
    attrName, effectLabel, createDiv, createSpan, createButton, showHint, setLabel,
    createExecTopBar,
} from '../ui';
import { withSpeakerAvatar, withLineAvatars } from '../npcAvatar';
import { assistantSystem } from '../../systems/AssistantSystem';
import { varietyPromoSystem } from '../../systems/VarietyPromoSystem';
import { MediaPanel } from './MediaPanel';

/** 角色可试镜判定已迁 ScriptSystem.isRoleAuditionable（2026-10-27 经纪人工作台批：试镜周与
 *  经纪人页剧本册共用一条筛，⛔ 两处各写一份）；这里的 roleLabel 只管角色标签文案。 */

/** 旁白态说话人：无头像无姓名=非对话句（galgame 叙述行）——行程文案与随机事件/试镜事件共用同一形态 */
const NARRATOR: DialogSpeaker = { id: 'narration', name: '', noAvatar: true };

/** 小助理说话人（剧宣行程赴约 VN 的事后一句——剧宣归助理域；头像走 withLineAvatars 出图） */
const ASSISTANT_SPEAKER: DialogSpeaker = { id: 'assistant', name: '夏夏', avatarChar: '夏' };

/** 获取角色标签文本（如「女主角 苏晴」或「飞行嘉宾」） */
function roleLabel(role: ScriptDef['roles'][0]): string {
    return role.name ? `${role.roleType}「${role.name}」` : role.roleType;
}

// 剧本类型中文名 genreName 随卡体抽公共迁至 ui/roleCard.ts（2026-10-25 样式对齐批）

/** 训练课（course）→ 场景键：五处场所归并，名单与 docs 场景清单表一一对应 */
const COURSE_SCENE: Record<string, SceneKey> = {};
// 练功房：体能三档 + 武术特训 + 礼礼仪态(T1) + 气场美学(T3)
['STAMINA_1', 'STAMINA_2', 'STAMINA_3'].forEach(k => { COURSE_SCENE[`SCHEDULE_COURSE_${k}`] = 'gym'; });
COURSE_SCENE.SCHEDULE_COURSE_SPECIAL_MARTIAL = 'gym';
COURSE_SCENE.SCHEDULE_COURSE_TEMPERAMENT_1 = 'gym';
COURSE_SCENE.SCHEDULE_COURSE_TEMPERAMENT_3 = 'gym';
// 书房：eq 三档 + wisdom 三档 + 艺术修养(T2)
['EQ_1', 'EQ_2', 'EQ_3', 'WISDOM_1', 'WISDOM_2', 'WISDOM_3'].forEach(k => { COURSE_SCENE[`SCHEDULE_COURSE_${k}`] = 'study'; });
COURSE_SCENE.SCHEDULE_COURSE_TEMPERAMENT_2 = 'study';
// 造型间：charm 三档
['CHARM_1', 'CHARM_2', 'CHARM_3'].forEach(k => { COURSE_SCENE[`SCHEDULE_COURSE_${k}`] = 'styling'; });
// 琴房：artistry 三档（声乐/器乐/全能舞台）+ 唱跳特训（唱跳一体，气口功课在琴房）
['ARTISTRY_1', 'ARTISTRY_2', 'ARTISTRY_3'].forEach(k => { COURSE_SCENE[`SCHEDULE_COURSE_${k}`] = 'musicroom'; });
COURSE_SCENE.SCHEDULE_COURSE_SPECIAL_SINGDANCE = 'musicroom';
// 排练室：talent 三档 + 主持特训（模拟直播实战，与舞台排练同场）
['TALENT_1', 'TALENT_2', 'TALENT_3'].forEach(k => { COURSE_SCENE[`SCHEDULE_COURSE_${k}`] = 'rehearsal'; });
COURSE_SCENE.SCHEDULE_COURSE_SPECIAL_HOST = 'rehearsal';

export class MonthProgressPanel {
    readonly element: HTMLElement;

    /** 公共顶栏（与决策日 1:1，2026-10-04 抽公共） */
    private readonly topBar = createExecTopBar();
    private readonly schedName: HTMLElement;
    private readonly schedType: HTMLElement;
    /** 中央卡资源变动行（体力/金钱，2026-10-05 用户令：资源变动上卡、属性变动留对话框） */
    private readonly popChanges: HTMLElement;
    /** 试镜周内容（挂进 VN 卡内容槽）：册行 / 详情 / 完成试镜键——行程文案框已退役，
     *  底部叙事=同一个 VnDialog 的旁白态（2026-10-06 用户令：无头像姓名的 VN 对话框） */
    private readonly shelfRow: HTMLElement;
    private readonly popDetail: HTMLElement;
    /** 试镜周唯一推进键行（完成试镜，2026-10-05 用户令） */
    private readonly weekActions: HTMLElement;
    /** 中央快闪行程卡 */
    private readonly weekPop: HTMLElement;
    /** 行程卡薄荷块（强制休息周切警示红底，同旧版 .mp-forced） */
    private readonly weekMint: HTMLElement;
    /** 场景背景层（容器，overflow hidden 裁掉图底部超出；缺图=纸白底） */
    private readonly sceneEl: HTMLElement;
    /** 背景图元素（CSS：宽度永远撑满，min-height:100% 兜底防底部露纸底） */
    private readonly sceneImg: HTMLImageElement;
    /** 当前已申请的场景图 URL（防重复加载/过期 onload 回写） */
    private appliedScene = '';

    /** 逐周状态 */
    private current: WeekResult | null = null;
    private popOpen = false;
    private popTimer: number | null = null;
    /** 事件待演（卡自动收后接事件 VN；试镜周延后到完成试镜） */
    private eventPending = false;

    /** 片场偶遇 VN 演出浮层（与事件 VN 共用同一实例） */
    private readonly vn = new VnDialog();
    /** media 弹窗（热议帖/新闻快讯，特殊剧情三载体之一） */
    private readonly mediaPanel = new MediaPanel();
    /**
     * 本面板的剧情演出档（执行宿主）：排干逻辑与出行地图、决策页共用 `PlotPlayer`（2026-10-14 抽公共），
     * 这里只交代三张嘴——VN 用本面板那张常驻卡、media 卡弹进本面板的 MediaPanel、
     * 演完回 `afterEncounter`（2026-10-24 茄子令三条改版：剧情排在偶遇**之后**＝本周内容收口处垫底，
     * 不再在事件读毕处插队——弹窗不许压着对话框、且要在进周末（背景换地图）之前弹）。
     */
    private readonly plotPlayer = new PlotPlayer('execution', {
        playLines: (lines, onEnd) => this.vn.play(lines, onEnd),
        showMedia: media => this.mediaPanel.show(media),
        onDrained: () => this.afterEncounter(),
    });
    private encounterResult: EncounterOutcome | null = null;

    /** 本周是否按「试镜周」处理（执行前快照，读档/状态变化后仍准确） */
    private auditionWeek = false;
    /** 试镜周已点过完成试镜（按钮自此收起，剧本池页解除——2026-10-05 用户令） */
    private auditionDone = false;
    /**
     * 本周试镜选定的剧组（2026-10-27 茄子改版「一次试镜行程只面一个剧组」）：null=选组层，
     * 非 null=组内角色层。会话内存不落存档（读档点在月初，试镜中读档回到选组层重选，无账目风险——
     * 与 auditionFailedWeek 同口径）；选组后只要该组还没试过任何角色就允许返回重选，
     * 试过即锁定（换组=两周面两组，违背「一次一组」约束）。
     */
    private auditionPickedScript: string | null = null;
    /** 本周剧本池是否为空（空=试镜周退化成普通周：文案+三角+点进，无按钮） */
    private shelfEmpty = false;
    /** 池内试镜记录（本月内防重复试镜） */
    private poolTried: Record<string, boolean> = {};
    /** 池详情当前聚焦（角色级） */
    private poolDetailIdx: { scriptId: string; roleIndex: number } | null = null;

    /** 竞角结果队列（定角夜=队列逐个直弹轻档；2026-10-30 茄子实测令，聊天列表页退役） */
    private revealResults: import('../../systems/ScriptSystem').RevealResult[] = [];
    private revealHandled: Set<string> = new Set();
    /** 队列游标（revealResults 下一位）；消费入口=app 入场链 ④ */
    /** 队列全弹完（app 注入：跑收口夜乙段——玩家表态完了才派剩下的位） */
    onRevealDone: (() => void) | null = null;
    /** 揭晓弹窗（队列直弹 · 轻档戏票，强制二选一；宿主由 app 注入 shell 顶层） */
    private revealLight!: ContractSignPop;

    /** 试镜对比演出（2026-10-05 改版）：详情框内就地切换，逐项对比门槛与自身属性；定时器防 destroy 泄漏 */
    private auditionTimers: number[] = [];
    /** 掷骰演出链的取消函数（destroy 时整链停掉，防在已摘除的 DOM 上复活 vn.play） */
    private diceCancel: (() => void) | null = null;

    /** 周末相位（自由出行批 2026-10-11；2026-10-12 茄子令撤二选一卡后收成一个枚举一字段）：
     *  `idle`=本周还没走到周末 / `outing`=满屏地图开着 / `done`=本周末已收口。
     *  插槽=周内容彻底读毕之后、进下一周/收月之前（`advanceFromWeek` 出口处），**每周一次**（末周先过周末再收月）。
     *  两态读法各自派生：守卫只挡 `outing`（推进链见 `tryAdvance`），进周末判据只认 `!== 'done'`。
     *  ⛔ 退回"一个字符串 + 一个布尔互相纠偏"的老写法——`''` 曾同时代表"还没到"和"已经过了"，
     *  收口时复位空恰好撞上"还没过周末"判据=无限周末循环（实机首夜抓到），根因是一个值两种读法。 */
    private weekend: 'idle' | 'outing' | 'done' = 'idle';
    /** 满屏小地图（周末出门用；懒建——不进周末不进 DOM） */
    private freeroam: import('../FreeroamMap').FreeroamMap | null = null;
    /** 本周到点赴约的队列（多槽批 2026-10-29）：按挂约顺序快照，逐条 shift 演出；同周多约依次演、互不顶替 */
    private dateQueue: { npcId: string; week?: number }[] = [];
    /** 本周末是否已演过至少一场赴约（队列见底时区分「纯赴约收口」与「坏档兜底照常出行」） */
    private datePlayed = false;

    constructor(private app: GameApp) {
        // 全屏面板：场景背景层（图未就位/加载失败=纸白底）
        const panel = createDiv('panel-root month-full');
        const scene = createDiv('month-scene');
        const sceneImg = document.createElement('img');
        sceneImg.className = 'month-scene-img';
        sceneImg.alt = '';
        scene.appendChild(sceneImg);
        panel.appendChild(scene);   // ⚠️ 此行曾被改构造器时误删 → 背景层不进 DOM 全屏白底（2026-10-08 茄子实测抓到）
        this.sceneEl = scene;
        this.sceneImg = sceneImg;

        // 公共顶栏（与决策日 1:1；属性抽屉内部自管）
        panel.appendChild(this.topBar.root);
        // 周进度点：顶栏正下方悬浮（属性抽屉展开时被盖，茄子接受）
        const wd = createDiv('wd-float');
        for (let i = 0; i < GameConfig.WEEKS_PER_MONTH; i++) wd.appendChild(createSpan('wd'));
        panel.appendChild(wd);

        // 中央快闪行程卡（卡片内部=旧版（全屏化前）行程卡同款：mint-block mp-block + mint-block-title + week-sched-title/type）
        const weekPop = createDiv('week-pop');
        const popInner = createDiv('week-pop-inner');
        const mint = createDiv('mint-block mp-block');
        this.weekMint = mint;
        mint.appendChild(createSpan('mint-block-title', '本周已安排行程'));
        this.schedName = createDiv('week-sched-title');
        mint.appendChild(this.schedName);
        // 类别行 + 资源变动同行（2026-10-05 用户令：变动行挪上类型行、删虚线分隔）
        const typeRow = createDiv('sched-type-row');
        this.schedType = createDiv('week-sched-type');
        typeRow.appendChild(this.schedType);
        this.popChanges = createDiv('week-changes');
        this.popChanges.style.display = 'none';
        typeRow.appendChild(this.popChanges);
        mint.appendChild(typeRow);
        popInner.appendChild(mint);
        weekPop.appendChild(popInner);
        panel.appendChild(weekPop);
        this.weekPop = weekPop;   // ⛔ 无点击收卡（2026-10-06 用户令）：卡片只随 0.9s 到期或周推进换新

        // 底部叙事框 = VnDialog 旁白态本体（2026-10-06 用户令：行程文案框=没有头像姓名的 VN 对话框，
        // 对话句/非对话句共用同一个组件同一张卡；事件/偶遇/行程文案全走 this.vn）
        this.vn.stayOpen = true;                                   // 常驻：旁白读毕不收卡，推进由宿主回调决定
        this.vn.nextSuppressed = () => this.detailOpen;            // 详情/演出态让位（池子页那半边交回组件：hold 段天然不出 ▶）
        // 试镜周内容挂进组件内容槽（卡内：正文→变动行→选项→▶→槽）
        this.shelfRow = createDiv('shelf-row');
        this.shelfRow.style.display = 'none';
        this.vn.contentSlot.appendChild(this.shelfRow);
        this.popDetail = createDiv('pop-detail');
        this.popDetail.style.display = 'none';
        this.vn.contentSlot.appendChild(this.popDetail);
        // 试镜周唯一推进键：完成试镜＝本段的句号（2026-11-03 统一收口模型：这颗钮与选项钮同族，
        // 按下去走 `vn.close`，段的收口回调＝`finishAuditionWeek`；不许绕过卡自己接流程——那样这段永远占着卡）
        this.weekActions = createDiv('week-actions');
        this.weekActions.appendChild(createButton('pool-cta', '完成试镜', () => this.vn.close()));
        this.weekActions.style.display = 'none';
        this.vn.contentSlot.appendChild(this.weekActions);

        // 揭晓弹窗（队列直弹 · 轻档戏票，强制二选一；同族壳与直签重档共用 ContractSignPop——
        // 宿主由 app 注入 shell 顶层，5c 挪决策页背景后面板容器是 hidden-panel ⛔ 塞面板）
        this.revealLight = new ContractSignPop(createDiv('_reveal_host_pending'));

        // 片场偶遇/事件 VN 演出浮层
        panel.appendChild(this.vn.element);
        // media 弹窗（热议帖/新闻快讯）：挂外壳不挂本面板根（2026-10-16 弹窗统一批）。
        // 本面板根带 .z-month-top，弹窗开着时它整体降到 z1，卡是压得住页面了，但底部导航条是 .panel-container
        // 的兄弟节点、无 z、DOM 靠后，挂在根里就跟着一起被裁在导航底下，剧情私信弹窗弹出时那排按钮还按得动。
        this.app.overlayRoot.appendChild(this.mediaPanel.element);
        this.mediaPanel.onDone = () => this.onMediaDone();
        // 旁白态与对话态是同一张卡（行程文案框退役），站内⛔ 让位切换——事件/偶遇播出时内容槽隐藏即可。
        // 唯一的例外＝周末出行期：那张卡 `display:none` 让位（`.month-full.fr-outing > .vn-dialog`），
        // 出行期唯一叙事通道是覆盖层自带的 VnDialog（2026-10-12 甲案，此前实测两张 `.vn-dialog.show` 同屏）
        this.vn.contentSlot.style.display = '';

        // 周末阶段⛔ 再经一张二选一卡（2026-10-12 茄子令「周末就直接进入地图，底部加一个在家休息的选项」）：
        // 当周读毕直接开满屏地图，宅家与出行的分岔都在地图底部那颗钮上（见 FreeroamMap.onFooter）

        // 点屏幕推进（排除按钮/文案框内操作/VN）
        panel.addEventListener('click', e => this.onScreenClick(e));

        this.element = panel;
    }

    /** 进入面板：开始月度执行（confirmMonth 已把 week 置 1），执行第 1 周 */
    refresh(): void {
        const state = GameState.getInstance();
        this.encounterResult = null;
        this.auditionWeek = false;
        this.auditionDone = false;
        this.auditionPickedScript = null;
        this.weekend = 'idle';
        this.current = gameLoop.runWeek();
        // ⚠️ update 在 runWeek **之后**：行程扣钱发生在周执行里，先刷会把扣钱前的旧数
        // 一路挂到本周周末（出行期信息栏常驻可见，「金钱 ¥1.0万」vs 实际 ¥8000，2026-10-11 实测）
        this.topBar.update(state);
        this.showWeekPop();    // 先亮卡再渲染：周点先亮，叙事随后打字
        this.renderWeek();
        // 偶遇⛔ 在这里播：叙事 VN 已占着同一张卡（vn.open 恒真）——改由 onNarrationAdvance 在叙事读毕后接播
        // 首周一次性「点击推进」hint（2026-10-25 新手引导批点位 3）：只在第一次进执行页的首周弹，
        // flags['tutorial:week'] 周目内一次（新周目 createNewGame 归零重教）；试镜周的推进走
        // 「完成试镜」键，不教点屏——首周大概率是它，这一档就先不弹（下次普通周再教）。
        // 周次取 this.current.week（runWeek 已把 player.week 递增到 2，读它会永远差一）
        if (this.current?.week === 1 && !this.auditionWeek && !state.hasFlag('tutorial:week')) {
            state.setFlag('tutorial:week');
            showHint('点一下屏幕，就能接着往下走');
        }
    }

    destroy(): void {
        this.clearAuditionTimers();
        this.diceCancel?.();
        this.freeroam?.destroy();
        this.vn.destroy();
    }

    // ===== 推进（普通周=点屏幕或点对话框；试镜周=只认「完成试镜」按钮） =====

    private onScreenClick(e: MouseEvent): void {
        const t = e.target as HTMLElement;
        if (t.closest('button, .toggle-bar, .script-book, .pool-detail, .shelf-row')) return;   // 控件/册子/详情自己消化
        this.tryAdvance();
    }

    /** 推进判定（点屏共用；点框=组件 element 监听同入此路）：全部收敛进 VN 组件——
     *  旁白打字中=补全、读毕=onNarrationAdvance（池子页拦/事件/下周）、事件中=逐句推进。
     *  周末（`weekend==='outing'`，满屏地图开着）= 第四道守卫：推进全封死，出口只有地图底部那颗钮。 */
    private tryAdvance(): void {
        if (this.detailOpen) return;   // 详情/试镜演出态：框内自己消化
        if (this.weekend === 'outing') return;   // 满屏地图开着：⛔ 点屏推进（周末⛔随机事件照出行周口径——
        //   周末插槽在周内容读毕之后，此时 eventPending 恒已清，不存在押后补播面）
        this.vn.advance();   // 快闪卡对点击透明（2026-10-06 用户令：收卡不由点击决定）——0.9s 内推进=卡片就地换新周内容重计时
    }

    /** 旁白读毕回调（VN 常驻模式段尾触发）：下一个行为=事件（读毕进下周，见 onEventEnd）或直接下一周。
     *  试镜周不走这里——那一段的句号是册页里那颗「完成试镜」，收口回调直接是 `finishAuditionWeek`
     * （2026-11-03 统一收口模型：防误触＝`vn.hold()`，不许在面板层再拦一道） */
    private onNarrationAdvance(): void {
        if (this.eventPending) { this.playEvent(); return; }
        this.finishWeekContent();
    }

    /**
     * 本周内容的收口出口（周叙事读毕、随机事件读毕，两条路都收到这里）。
     * 顺序（2026-10-24 茄子令改版后）：同周还挂着待播偶遇就先播它，没有才轮到剧情垫底（`afterEncounter`）。
     * 2026-10-16 修的旧账（剧情先于偶遇）已于 2026-10-24 调向：剧情挪到偶遇**之后**垫底——
     * 杀青微博那类 media 弹窗要等本周行程（含偶遇）彻底演完、对话框收掉后再弹（见 afterEncounter）。
     *
     * 偶遇为何挂在这里（2026-10-10 修 bug 的原注释）：原先挂在 refresh/nextWeek 尾部，那时周叙事已占用
     * 同一张 VN 卡（`vn.open` 恒真），所以偶遇从未演出，掷出来的那一发当周就凭空消失。
     * 本出口只负责「什么时候轮到它播」；「这部戏遇过几次」那本账（首拍周保底与 claim 盖章）
     * 已于同日由茄子令整条撤销，掷点方与这里都不再记账。
     */
    private finishWeekContent(): void {
        if (this.current?.encounter && !this.encounterResult) { this.maybePlayEncounter(); return; }
        this.afterEncounter();
    }

    /**
     * **偶遇读毕／本周内容收口的垫底出口**（2026-10-24 茄子令三条件改版）：
     * 顺序＝随机事件 → 偶遇 → **剧情演出档（media 弹窗/VN 剧情）** → 周推进（周末/下周/收月）。
     *  - **弹窗不许与对话框同屏**：队列有可演的拍时先把常驻 VN 卡 `stop()` 收掉再排干——
     *    事件/偶遇的末句还挂在卡上时弹 MediaPanel 就是「弹窗下面压对话框」（茄子实测现象）。
     *  - **背景保持当周行程场景**：本口在 `advanceFromWeek`（其首分支=进周末地图）**之前**，
     *    scene 此刻还是本周的，弹窗底下就是片场/试镜间那层，不是小地图。
     *  - 空队列不动卡（stayOpen 常驻旁白照旧，周推进时自然换卡）。
     */
    private afterEncounter(): void {
        if (this.hasExecutionPlot()) {
            this.vn.stop();                 // 收掉常驻卡（读毕回调语境，无打字可打断）
            if (this.tryPlayPlot()) return; // 推进权在排干器（media 关卡/剧情读毕都会回本口）
        }
        this.advanceFromWeek();
    }

    /** 执行宿主队列里有没有可演的拍（message 拍月底才投递，不算——判据与 PlotPlayer.playNext 同一把） */
    private hasExecutionPlot(): boolean {
        const plot = PlotSystem.getInstance();
        return plot.pendingFor('execution').some(m => {
            const e = plot.eventById(m.eventId);
            return !e || !isChatBeat(e, m);
        });
    }

    /**
     * 剧情演出档（2026-10-13 茄子令「任何问题都要治本」＝形状 B「效果先结算、句子后播」拆掉；
     * 2026-10-14 排干逻辑抽进 `plotPlayback.ts`，本面板与出行地图、决策页同吃一份）。
     * 返回 true＝本周期的推进改由演出回调接手（同一张 VN 卡不许两场并播，偶遇档排在它后面）。
     * 只演 `playOn:'decision'` 之外的那一片（播出侧那类已挪到决策页，见 `FixedEventDef.playOn`）。
     */
    private tryPlayPlot(): boolean {
        return this.plotPlayer.playNext();
    }

    /** MediaPanel「知道了」回调：推进剧情队列，接着演下一条 */
    private onMediaDone(): void {
        this.plotPlayer.onMediaDone();
    }

    // ===== 框内状态判定/按钮显隐（单一真相源：规则只写在这里——2026-10-05 用户令「不要拆东墙补西墙」） =====

    /** 详情/试镜演出态：pop-detail 展开中（框内自己消化，点屏/点框不推进） */
    private get detailOpen(): boolean {
        return this.popDetail.style.display !== 'none';
    }

    /** 「完成试镜」键显隐规则：组内角色层（已选组）且未点过；选组层 ⛔ 给键——一次一组，
     *  选组只是导航还没面任何角色（2026-10-28 茄子令），进了组内才谈「完成」；详情/演出态让位。
     *  「未完成」之外不再叠第二道判据：推进权的拦截归组件（hold），这里只管这颗钮长在哪儿 */
    private syncWeekActions(): void {
        this.weekActions.style.display =
            this.auditionWeek && !this.auditionDone && this.auditionPickedScript != null && !this.detailOpen ? '' : 'none';
    }

    /** **册页那一段的收口回调**（句号＝「完成试镜」那颗钮按下，即 `vn.close()` 收口到这里；2026-10-06 茄子终版澄清）——
     *  随机事件只是放出来演：与全站随机事件同一节奏（VN 内点击逐句推进），**事件读毕（玩家点掉最后一句）
     *  就交回本周内容收口**（见 onEventEnd；试镜周掷不到偶遇，所以实际就是进下一周）；
     *  无事件则按键直接进下一周。
     *  同日茄子令：内容槽（剧本册/按钮都挂在这）随本周结束一起收起——它是「本周还要读」的载体，
     *  本周既已了结，此后本周期内任何一张卡（宅家旁白、剧情演出、偶遇）底下都不该再顶着册行。
     *  显隐交回下一周 `renderWeek` 重定（那里按新的 `auditionWeek` 重新摆）。
     * 注意 本方法**只从段的收口回调进来**（不许再挂到别的钮上）：它前面那张卡已由 `close` 结掉，
     *  后面的宅家/剧情/偶遇才排得上队（2026-11-03 修掉的就是「流程走了、卡没结」）。 */
    private finishAuditionWeek(): void {
        this.auditionDone = true;                       // 试镜彻底结束：按钮收起、防误触撤除
        this.auditionPickedScript = null;               // 选组态一并清（本周期内不会再回册行）
        this.syncWeekActions();
        this.vn.contentSlot.style.display = 'none';     // 册行整槽收口（2026-10-14 茄子令：收口点提到这里）
        if (this.eventPending) { this.playEvent(); return; }
        this.afterEncounter();   // 剧情垫底与普通周同轴（2026-10-24 改版：见 afterEncounter 注释）
    }

    /** 周内推进出口：末周收月、否则进下周（tryAdvance 与完成试镜共用同一语义）。
     *  自由出行批（2026-10-11）：周内容彻底读毕后先过「周末阶段」，周末收口（`weekend==='done'`）
     *  才走真正的推进（末周=先过周末再收月）。 */
    private advanceFromWeek(): void {
        if (this.weekend !== 'done') { this.enterWeekend(); return; }
        if (this.current?.isLast) { this.finish(); return; }
        this.nextWeek();
    }

    /** 周末阶段入口：有「已定本周」的赴约先自动演出（选周自动化 2026-10-28；多槽批 2026-10-29 同周多约依次演），
     *  否则满屏地图铺开（2026-10-12 茄子令撤二选一卡「周末就直接进入地图」）。
     *  茄子 10-11 令：出行期保留执行页信息栏/周点条/属性抽屉——地图=全屏背景层（参照行程背景，
     *  抽屉展开⛔ 推移地图），三者 z 提到地图上（`.fr-outing`），花钱/探班后刷新顶栏。
     *  ⚠️ 桩用同名方法直达周末（`_test_freeroam_*.mjs` 三支），入口形状变了、名字没变。 */
    private enterWeekend(): void {
        this.eventPending = false;
        // 选周自动赴约（茄子 10-28 令「选哪个就在哪个周末自动赴约」）：week 相等即到点，不铺地图直接演。
        // 多槽批 2026-10-29：**周级互斥**（2026-10-29 茄子令「一周只能安排一次」）——正常每周 0~1 条；
        // 队列仍按数组序兜底坏档多条，依次演、互不顶替；
        // `week` 未定的挂起不算（约未成形，周末照常出行）；纯赴约=全演完收口（2026-10-28 拍板），不出行。
        const wk = this.current?.week;
        const due = (GameState.getInstance().player.pendingInvites ?? []).filter(i => i.week && i.week === wk);
        if (due.length) {
            this.dateQueue = due;
            this.datePlayed = false;
            this.playNextDate();
            return;
        }
        // 品牌活动/综艺录制到点自动赴约（自由日活动呈现批 2026-10-31）：定档周周末自动演出活动 VN，
        // 纯赴约收口不出行（同邀约口径）；账不在这里结——费用/热度/名气挂录制月月结（VN 是呈现层）
        if (this.playActivityVn()) return;
        this.openWeekendMap();
    }

    /** 铺满屏地图（周末出行态）：正常周末入口与「坏档兜底」共用这一条 */
    private openWeekendMap(): void {
        this.weekend = 'outing';
        if (!this.freeroam) {
            this.freeroam = new FreeroamMap(this.app);
            this.element.appendChild(this.freeroam.element);
        }
        this.element.classList.add('fr-outing');
        this.freeroam.open(stayedHome => this.onWeekendEnd(stayedHome),
            () => this.topBar.update(GameState.getInstance()));
    }

    /**
     * 赴约周末（选周自动化 2026-10-28；周级互斥 2026-10-29：正常每周 0~1 条，队列是坏档兜底）：
     * 演出赴约 VN（好感 +INVITE_WEEKEND_GAIN，账在 `resolvePendingInvite` 当场结清落盘），底图=咖啡馆、正脸头像与偶遇同形制。
     * 队列里还有约 ⇒ 播完接着演下一条（兜底）；见底 ⇒ 收口。
     * `weekend` 保持非 'outing'：tryAdvance 那道守卫封的是地图态，赴约卡要点屏推进。
     * 纯赴约（茄子 10-28 拍板）：全演完走 `onWeekendEnd` 收口进下周，不出行、不兼职。
     */
    private playNextDate(): void {
        const inv = this.dateQueue.shift();
        if (!inv) {
            // 队列见底：同周还压着活动（互斥下不该发生，坏档兜底按序演）→ 活动收口；
            // 演过至少一场＝纯赴约收口（不出行）；一场都没演成（坏档）＝照常铺地图兜底
            if (this.playActivityVn()) return;
            if (this.datePlayed) this.onWeekendEnd(false);
            else this.openWeekendMap();
            return;
        }
        const res = freeroamSystem.resolvePendingInvite(inv);
        if (!res.ok) {
            // 保险丝：坏项直接丢、继续下一条（⛔ 原样重入＝死递归）
            this.playNextDate();
            return;
        }
        this.datePlayed = true;
        const speaker: DialogSpeaker = { id: res.npcId, name: res.npcName, avatarChar: res.npcName.slice(0, 1) };
        const lines = res.lines.map(l => ({ speaker, text: l.text, ...(l.changes ? { changes: l.changes } : {}) })) as DialogLine[];
        this.applyScene('cafe');
        withSpeakerAvatar(speaker, () => this.vn.play(lines, () => this.playNextDate()));
    }

    /**
     * 活动周自动赴约 VN（自由日活动呈现批 2026-10-31）：定档周到点的品牌活动/综艺录制，
     * 演两句（活动现场旁白 + 经纪人事后一句）即收口进下周（纯赴约口径，不出行不兼职）。
     * 复用咖啡馆邀约自动赴约链的同一形态（vn 卡点屏推进、`weekend` 保持非 'outing'）；
     * **只呈现不结账**——费用/热度/名气挂录制月月结（monthlySettle 兜底保留，刷新重演无副作用）。
     * @returns 是否演了一场（调用方据此短路地图入口）
     */
    private playActivityVn(): boolean {
        const wk = this.current?.week;
        if (!wk) return false;
        const p = GameState.getInstance().player;
        const abs = p.year * 12 + p.month;
        // 2026-11-02 起品牌活动改工作周自排（执行时结算、无赴约 VN），这里只剩综艺/宣发行程
        const booking = varietyPromoSystem.bookingDueAt(abs, wk);
        const data = booking ? varietyPromoSystem.recordVnLines(booking) : null;
        if (!data) return false;
        this.datePlayed = true;
        // 底图按形态走：路演=活动现场、综艺录制=录制棚（剧场）、媒体专访=棚内
        const scene: SceneKey = booking?.showId === 'PROMO_ROADSHOW' ? 'event'
            : booking?.showId === 'PROMO_INTERVIEW' ? 'photoshoot'
                : 'theater';
        this.applyScene(scene);
        this.vn.play(
            [
                { speaker: NARRATOR, text: data.narrator },
                // 剧宣行程事后=小助理（对内日程，2026-11-01 实机反馈）
                { speaker: ASSISTANT_SPEAKER, text: assistantSystem.joinTerm(data.followup) },
            ] as DialogLine[],
            () => this.onWeekendEnd(false),
        );
        return true;
    }

    /** 周末收口（底部那颗钮、赴约周末的 VN 读毕、或演出读完都走这一条；`stayedHome`=点的是「在家休息」，
     *  体力账已由 `FreeroamSystem` 结清，这里只补旁白）。
     *  相位落 `done`——它⛔ 挡点屏推进，所以宅家旁白的点击天然推得动：老写法要在演出前先把相位
     *  复位成空来绕开守卫（`:383` 那句"实机死锁：收口句永远点不过去"），根因就是一个值两种读法。
     *  卡底下只许留文案+变动行——剧本册那一路归 `finishAuditionWeek` 收口（见那里），这里不再叠第二道。
     *  背景跟文案走：宅家就切「家」（2026-10-14 茄子实测：出地图时背景仍是进组那间的场景）。 */
    private onWeekendEnd(stayedHome: boolean): void {
        this.element.classList.remove('fr-outing');
        this.weekend = 'done';
        this.topBar.update(GameState.getInstance());
        // 周末收口也走垫底出口（2026-10-24）：正常路径剧情早在进周末前排干（afterEncounter 在
        // enterWeekend 之前），这里只是兜底——不让漏网条目掉到下个周末的地图宿主去弹（背景错）
        if (!stayedHome) { this.afterEncounter(); return; }
        this.applyScene(this.stayHomeScene());
        this.vn.play(
            [{
                speaker: NARRATOR,
                text: '你宅家过了个周末，哪儿也没去。',
                changes: [{ label: '体力', delta: `+${GameConfig.WEEKEND_REST_ENERGY}` }],
            }],
            () => this.afterEncounter(),
        );
    }

    /** 宅家旁白的背景：本周本来就在家/度假就原地不动（度假周把人拽回家是倒退），
     *  其余（试镜间/片场/棚/练功房…）一律切「家」——文案说的是宅家，背景就得是家。 */
    private stayHomeScene(): SceneKey {
        const w = this.current;
        if (!w) return 'home';
        const weekScene = this.sceneKeyForWeek(w);
        return weekScene === 'home' || weekScene === 'vacation' ? weekScene : 'home';
    }

    /** 事件 VN 统一收口（2026-10-06 茄子终版令）：事件读毕=本周执行彻底完毕，不再回到本周行程文案
     *  （事件属于本周执行的一部分，读毕即执行完毕；任何情况下不回到本周行程文案）。
     *  事件本身仍靠玩家点击逐句推进（VN 组件行为），此处的推进是玩家点掉最后一句的后果，非自动跳过。
     *  2026-10-16 茄子令「随机事件与偶遇分家」：读毕之后不许直接跳下周——同周那张偶遇卡先补播
     *  （事件不再拦掷点，`GameLoop.runWeek` 里那道 `!event` 已撤），出口与周叙事/剧情共用 `finishWeekContent`。 */
    private onEventEnd(): void {
        this.eventPending = false;
        // 事件读毕不接剧情（2026-10-24 茄子令：剧情垫底到偶遇之后，见 afterEncounter）——
        // 旧「事件后先弹剧情」的插队撤掉，弹窗曾因此压着事件对话框、还抢在偶遇前面
        this.finishWeekContent();
    }

    private nextWeek(): void {
        const state = GameState.getInstance();
        // 落盘节奏（2026-11-03 茄子令改月存）：周界**不写盘**——合法档只有一格＝每月月初的决策日
        // （`GameLoop.doFinishMonth` 尾部那次），月内一切退出＝重玩本月。
        // 旧口径（10-28 的「每周存一次」）留着没跟着改的坑：`GameState.load` 会清掉本月排期，
        // 于是「第3周初」那份档读回来人被放回决策日、四周行程全空——账对不上（茄子 11-03 实测抓到）。
        this.encounterResult = null;
        this.auditionWeek = false;
        this.auditionDone = false;
        this.auditionPickedScript = null;
        this.weekend = 'idle';   // 新周从行程开始（上周周末已在 `onWeekendEnd` 收口）
        this.current = gameLoop.runWeek();
        this.topBar.update(state);   // 同 refresh：runWeek 之后刷，⛔ 挂扣钱前的旧数（2026-10-11）
        this.showWeekPop();    // 先亮卡再渲染（同 refresh：事件闸门依赖 popOpen 时序）
        this.renderWeek();
    }

    // ===== 中央快闪行程卡（0.9s 自动收；点击⛔收卡、推进到下一周=就地换新周内容重计时——2026-10-06 用户令） =====

    private showWeekPop(): void {
        this.weekPop.classList.add('show');
        this.popOpen = true;
        clearTimeout(this.popTimer);
        this.popTimer = window.setTimeout(() => this.dismissWeekPop(), 900);
    }

    private hideWeekPop(): void {
        this.weekPop.classList.remove('show');
        this.popOpen = false;
        clearTimeout(this.popTimer);
    }

    /** 收卡唯一通道：0.9s 自动到期（点击已⛔参与，见 showWeekPop 注） */
    private dismissWeekPop(): void {
        if (!this.popOpen) return;
        clearTimeout(this.popTimer);
        this.hideWeekPop();
    }

    // ===== 渲染 =====

    private renderWeek(): void {
        const w = this.current;
        if (!w) return;
        const state = GameState.getInstance();

        // 场景背景（按行程类型切换）
        this.applyScene(this.sceneKeyForWeek(w));

        // 强制休息周：行程卡切警示红底（旧版同款 .mp-forced，2026-10-05 用户令恢复）
        this.weekMint.classList.toggle('mp-forced', w.forcedRest);

        // 周进度点（顶栏下方悬浮条：前 week 个点亮）
        const dots = this.element.querySelectorAll('.wd-float .wd');
        dots.forEach((d, i) => d.classList.toggle('on', i < w.week));

        // 中央卡行程信息（信息=实机同字段）
        this.schedName.textContent = w.schedName;
        this.schedType.textContent = w.schedType;

        // 底部叙事（同一张 VN 卡的旁白态）：试镜周=入口短句+册行+完成试镜键；普通周=叙事+变动行
        this.auditionWeek = this.isAuditionWeek(w);
        this.shelfEmpty = false;
        this.shelfRow.style.display = this.auditionWeek ? 'flex' : 'none';
        this.syncWeekActions();   // 试镜周先给键；空池由 renderShelf 判定后再 sync 收起
        this.popDetail.style.display = 'none';
        this.vn.setBodySuppressed(false);   // 新周叙事恢复正文（详情/演出态让位过）
        this.vn.contentSlot.style.display = '';   // 内容槽恢复（事件期间藏过）
        // 变动分流：扣除的→中央行程卡；获得的→底部对话框；组内属性在前、资源在后
        const isRes = (c: WeekChange) => c.label === '体力' || c.label === '金钱' || c.label === '名气';
        const attrsFirst = (list: WeekChange[]) => [...list.filter(c => !isRes(c)), ...list.filter(isRes)];
        const renderChg = (host: HTMLElement, c: WeekChange) => {
            const span = createSpan('chg', `${c.label} `);
            span.appendChild(createSpan(`delta-in ${c.delta.startsWith('-') ? 'text-fail' : 'text-success'}`, c.delta));
            host.appendChild(span);
        };
        const losses = attrsFirst(w.changes.filter(c => c.delta.startsWith('-')));
        const gains = attrsFirst(w.changes.filter(c => !c.delta.startsWith('-')));
        // 行程叙事=旁白态（打字机/补全/变动行浮现/▶ 显隐全由组件管理，读毕回调=onNarrationAdvance）。
        // 试镜周的收口回调换成 `finishAuditionWeek`：这一段的句号不是点屏，而是册页里那颗「完成试镜」
        //（2026-11-03 统一收口模型＝与选项钮同族，见 VnDialog 头注释；空池分支在 renderShelf 里自己换回普通回调）
        // 新周内容一律 playNow 接管：上一段（上周旁白／试镜周册行旁白）可能还占着卡，
        // 它的收口不兑现——留着日后触发会把本周的推进提前吃掉（周次跳格）
        this.vn.playNow(
            [{ speaker: NARRATOR, text: w.description, changes: gains.length ? gains : undefined }],
            () => (this.auditionWeek ? this.finishAuditionWeek() : this.onNarrationAdvance()),
        );
        this.popChanges.innerHTML = '';
        if (losses.length > 0) {
            this.popChanges.style.display = '';
            losses.forEach(c => renderChg(this.popChanges, c));
        } else {
            this.popChanges.style.display = 'none';
        }
        if (this.auditionWeek) this.renderShelf();
        // 有册=推进权交给册页（点屏/点▶/空格全吞，▶ 不亮）；空池那支不交，它已经自己退化成普通周
        if (this.auditionWeek && !this.shelfEmpty) this.vn.hold();

        // 事件待演标记（卡自动收后接事件 VN）
        this.eventPending = !!w.event;
    }

    // ===== 场景背景（行程 → 场景映射，一处改表全改） =====

    /** 拍摄/签约进组→片场；舞台·直播·演出·活动·音乐→综艺棚；试镜→试镜间；休息/强制休息→家；训练→街道傍晚；空档兜底→城市夜景 */
    private sceneKeyForWeek(w: WeekResult): SceneKey {
        const state = GameState.getInstance();
        if (w.forcedRest) return 'home';
        const slot = state.player.schedule[w.week - 1] ?? null;
        if (!slot) return 'home';
        if (slot.kind === 'audition') return 'audition';
        if (slot.kind === 'rest') return slot.refId === 'SCHEDULE_REST_3' ? 'vacation' : 'home';
        if (slot.kind === 'course') return COURSE_SCENE[slot.refId] ?? 'study';
        if (slot.kind === 'work') {
            if (slot.endorseId) return 'event';   // 品牌活动周=活动现场（2026-11-02）
            if (slot.contractId) {
                // 签约进组：按合同剧本题材分古装/现代片场（尺在 `ScriptSystem.isCostumeScript`，
                // 影视城探班垫的底共用它；年代 Period 归现代片场）
                const contract = state.player.contracts.find(c => c.id === slot.contractId);
                return contract && scriptSystem.isCostumeScript(contract.scriptId) ? 'filmset-costume' : 'filmset-modern';
            }
            const trade = GameData.SCHEDULE_MAP[slot.refId]?.trade;
            if (trade === 'shoot') return 'photoshoot';
            if (trade === 'stage' || trade === 'performance') return 'theater';
            if (trade === 'event') return 'event';
            if (trade === 'music') return 'music';
            if (trade === 'live') return 'live';
            return 'theater';
        }
        return 'home';
    }

    /**
     * 贴背景图：当前图 high 优先级、decode 后可见；等待期容器显示该图主色底（非纸白），图可见即清底。
     * 三条既有契约不变：同图不重复贴、过期回调守卫、失败清标记允许下次重试。
     */
    private applyScene(key: SceneKey): void {
        const url = SCENE_BGS[key];
        if (this.appliedScene === url) return;
        this.appliedScene = url;
        // img 元素方案：CSS 侧 width:100%/min-height:100% 实现「宽度永远撑满，裁顶只上移；
        // 仅当按宽显示会底部露纸底时才放大保底」——切图先隐藏防闪旧图
        this.sceneImg.style.visibility = 'hidden';
        this.sceneImg.style.opacity = '0';
        applyScenePlaceholder(this.sceneEl, key);   // 主色 + LQIP 模糊小图，先于加载（等待期不再是白屏）
        markSceneActive(url);
        loadSceneBgImage(this.sceneImg, url, ok => {
            if (this.appliedScene !== url) return;
            if (!ok) {
                this.appliedScene = '';              // 图后补/改名后可重试
                this.sceneImg.style.visibility = 'hidden';   // 占位底留着兜底
                return;
            }
            this.sceneImg.style.visibility = 'visible';
            requestAnimationFrame(() => { if (this.appliedScene === url) this.sceneImg.style.opacity = '1'; });
            // 淡入结束后清占位（在此之前 LQIP 垫在下面，视觉是「模糊 → 清晰」而不是白屏）
            setTimeout(() => { if (this.appliedScene === url) clearScenePlaceholder(this.sceneEl); }, 280);
        });
        // 同一 tick 尾部启动预热：不能放进 load 回调，否则 404/慢网会让预热永远不启动
        scheduleScenePreheat();
    }

    // ===== 试镜周：框内剧本册（2026-10-27 两层化：先选剧组→组内逐角色点试，一次行程只面一个组） =====

    /** 册行入口：选组层/组内角色层二选一渲染（auditionPickedScript 是唯一分流真相源） */
    private renderShelf(): void {
        if (this.auditionPickedScript) this.renderRoleShelf(this.auditionPickedScript);
        else this.renderGroupPick();
        this.syncWeekActions();   // 选组/换层都重算「完成试镜」键（选组层无键、组内层给键）
    }

    /** 选组层：当期市场上「有可试角色」的剧组列表。空池=试镜周退化成普通周（2026-10-05 口径不变） */
    private renderGroupPick(): void {
        this.shelfRow.innerHTML = '';
        const groups = scriptSystem.auditionableScripts();
        if (groups.length === 0) {
            // 无合适剧本：试镜周退化成普通周（文案+三角+点屏/点框推进，⛔ 完成试镜键）——2026-10-05 用户令
            this.shelfEmpty = true;
            this.shelfRow.style.display = 'none';
            this.syncWeekActions();
            // 空池这句顶掉本周旁白（本周唯一要读的一句就是它）——用 playNow，不排队
            this.vn.playNow(
                [{ speaker: NARRATOR, text: '翻了一圈剧本，也没找到适合自己的本子，下个季度再来吧。' }],
                () => this.onNarrationAdvance(),
            );
            return;
        }
        // 有册=剧本池页（完成试镜键的显隐交给 syncWeekActions：池子页给键，点过即收）
        this.syncWeekActions();
        groups.forEach(s => {
            const auditionable = s.roles
                .map((role, roleIndex) => ({ role, roleIndex }))
                .filter(x => scriptSystem.isRoleAuditionable(s.id, x.role, x.roleIndex));
            const card = createDiv('script-book');
            card.setAttribute('data-script', s.id);
            // 组卡=原剧本卡同形同比例（2026-10-28 茄子令：卡面只留剧名——类型题材/角色概览都撤，
            // 信息由点进组内层再看；色条=组内可试角色的最高番位）
            const topLevel = Math.max(...auditionable.map(x => x.role.level));
            card.appendChild(createSpan(`tier-bar tier-${topLevel}`));
            card.appendChild(createSpan('sb-name', `《${s.name}》`));
            card.addEventListener('click', e => { e.stopPropagation(); this.pickGroup(s.id); });
            this.shelfRow.appendChild(card);
        });
    }

    private pickGroup(scriptId: string): void {
        this.auditionPickedScript = scriptId;
        this.renderShelf();
        // 到组文案（2026-10-28 茄子令）：入口句说「挑一个剧组」，选定后行程叙事跟着切到该剧组。
        // 只换文案不动状态机：推进权仍在册页手里（下面 playNow 之后重新 hold），点屏依旧推不动周
        const s = GameData.SCRIPT_MAP[scriptId];
        // 选组后换文案：本周旁白还占着卡（推进权在册页手里），换掉它才看得到到组这句。
        // 换完仍要 hold——句号还是那颗「完成试镜」，这一段的收口回调跟着换成 finishAuditionWeek
        if (s) {
            this.vn.playNow([{ speaker: NARRATOR, text: `来到《${s.name}》剧组试镜……` }], () => this.finishAuditionWeek());
            this.vn.hold();
        }
    }

    /** 组内角色层：只出选定剧组的可试角色行（2026-10-28 茄子令删「返回选组」——选定即锁定该组） */
    private renderRoleShelf(scriptId: string): void {
        const s = GameData.SCRIPT_MAP[scriptId];
        if (!s) { this.auditionPickedScript = null; this.renderGroupPick(); return; }
        this.shelfRow.innerHTML = '';
        // ⛔ 组头（2026-10-28 茄子抓「排版奇怪」后删）：卡面三行自带剧名，组头那行《剧名》纯重复；
        // 角色卡直接进横滑行，与原全池册子同形。
        // 能力 tag 缺口行（特训重做批 2026-10-30）：硬门槛不过=不进册；只差 requiredTag 的照列置灰——
        // tag 可以主动去特训补，藏起来玩家就不知道差什么（点击弹置灰原因，不进详情）
        const entries: { role: ScriptDef['roles'][0]; roleIndex: number; tagReason: string | null }[] = [];
        s.roles.forEach((role, roleIndex) => {
            if (!scriptSystem.isRoleAuditionable(s.id, role, roleIndex, { ignoreTagLock: true })) return;
            entries.push({ role, roleIndex, tagReason: scriptSystem.roleTagLockReason(role) });
        });
        entries.sort((a, b) => b.role.level - a.role.level);
        entries.forEach(({ role, roleIndex, tagReason }) => {
            const card = createDiv('script-book');
            card.setAttribute('data-script', s.id);
            card.setAttribute('data-role', String(roleIndex));
            if (tagReason) card.classList.add('is-tag-locked');
            const bar = createSpan(`tier-bar tier-${role.level}`);
            card.appendChild(bar);
            // 卡面三行=原全池册子同款（2026-10-28 茄子令恢复：剧名+角色定位+角色名，卡自成一体不依赖组头）
            card.appendChild(createSpan('sb-name', `《${s.name}》`));
            card.appendChild(createSpan('sb-role-type', role.roleType));
            card.appendChild(createSpan('sb-role-name', role.name ? `「${role.name}」` : ''));
            if (tagReason) {
                card.appendChild(createSpan('sb-lock', tagReason));
                card.addEventListener('click', e => { e.stopPropagation(); showHint(tagReason); });
            } else {
                if (this.isRoleAuditioned(s.id, roleIndex)) card.appendChild(createSpan('done-badge', '✓ 已试镜'));
                else if (scriptSystem.isAuditionLocked(s.id, roleIndex)) card.appendChild(createSpan('done-badge fail', '✗ 已失败'));
                card.addEventListener('click', e => { e.stopPropagation(); this.showPoolDetail(s, roleIndex); });
            }
            this.shelfRow.appendChild(card);
        });
    }

    private isRoleAuditioned(scriptId: string, roleIndex: number): boolean {
        return GameState.getInstance().player.auditionQualified.some(
            q => q.scriptId === scriptId && q.roleIndex === roleIndex
        );
    }

    /** 框内详情（紧凑单页）：信息=实机详情全字段，左右结构（角色介绍左/属性门槛右） */
    private showPoolDetail(s: ScriptDef, roleIndex: number): void {
        const role = s.roles[roleIndex];
        this.poolDetailIdx = { scriptId: s.id, roleIndex };
        this.shelfRow.style.display = 'none';
        const detail = this.popDetail;
        detail.style.display = '';
        this.vn.setBodySuppressed(true);   // 详情态：旁白正文整段让位（▶ 由 nextSuppressed 谓词同步隐藏）
        this.syncWeekActions();  // 详情/试镜演出态：周按钮让位

        const tried = this.isRoleAuditioned(s.id, roleIndex);
        const locked = !tried && scriptSystem.isAuditionLocked(s.id, roleIndex);
        // 门槛行红绿=试镜结果强调（2026-10-28 茄子定）：通过→绿一直显示（资格在池就绿）；
        // 本周失败锁着→红；没试过/锁已重置可重试→普通灰
        const reqTone = tried ? 'ok' as const : (locked ? 'no' as const : undefined);

        // 卡体走公共 roleCardHtml（试镜钮态：成功定格/失败锁定/可试）
        detail.innerHTML = roleCardHtml(s, role, { roleTitle: `试镜角色：${roleLabel(role)}`, reqTone })
            + `<div class="btn-row"><button class="pool-cta ghost" id="pdBack">返回</button>`
            + `<button class="pool-cta ${tried ? 'done' : (locked ? 'blocked' : '')}" id="pdGo" ${tried || locked ? 'disabled' : ''}>${tried ? '完成试镜 等待结果' : (locked ? '试镜失败 下个试镜周可再试' : '试镜')}</button></div>`;

        bindPdFold(detail);
        document.getElementById('pdBack')!.addEventListener('click', e => {
            e.stopPropagation();
            detail.style.display = 'none';
            this.renderShelf();
            this.shelfRow.style.display = 'flex';      // renderShelf 只重填内容，显隐在这里恢复（showPoolDetail 藏过）
            this.vn.setBodySuppressed(false);          // 回池子页：旁白正文恢复
            this.syncWeekActions();                    // 回池子页：键随 sync 恢复/收起
        });
        // 2026-10-05 改版：不达标也可试镜——判定与逐项对比在弹窗里演出（doAudition）
        if (!tried && !locked) {
            document.getElementById('pdGo')!.addEventListener('click', e => {
                e.stopPropagation();
                this.doAudition(s, roleIndex);
            });
        }
    }

    /** [试镜]：判定（纯门槛）→ 弹窗逐项对比动效演出 → 结论等玩家确认（2026-10-05 改版：不达标也可试） */
    private doAudition(s: ScriptDef, roleIndex: number): void {
        const result = scriptSystem.audition(s.id, roleIndex);
        const role = s.roles[roleIndex];
        const state = GameState.getInstance();
        const rows = role.requirements.map(req => ({
            label: attrName(req.attr),
            need: req.value,
            cur: state.getAttribute(req.attr),
            ok: state.getAttribute(req.attr) >= req.value,
        }));
        this.playAuditionCompare(s, roleIndex, role, rows, result.success);
    }

    /** 试镜对比演出（详情框内就地切换）：逐项自动连播（0.5s/项）→ 全亮完 0.6s 盖结论章 → 确定回详情 */
    private playAuditionCompare(
        s: ScriptDef,
        roleIndex: number,
        role: ScriptDef['roles'][0],
        rows: { label: string; need: number; cur: number; ok: boolean }[],
        success: boolean,
    ): void {
        this.clearAuditionTimers();
        this.poolDetailIdx = { scriptId: s.id, roleIndex };
        this.shelfRow.style.display = 'none';
        const detail = this.popDetail;
        detail.style.display = '';
        this.vn.setBodySuppressed(true);   // 演出态：旁白正文整段让位
        this.syncWeekActions();  // 演出态：周按钮让位（单一真相源，⛔ 手工 display——曾因定时器里 renderShelf 误亮回「完成试镜」出双按钮）
        detail.innerHTML = `<div class="aud-view">`
            // 作品+角色名即标题（居中，2026-10-06 用户令：删「试镜」小标题，章盖在标题上）；
            // 副行=角色类型徽章+角色名（主副层次，2026-10-06 用户令「再明显一些」）
            + `<div class="aud-role"><span class="aud-script">《${s.name}》</span><span class="aud-sub"><span class="aud-type">${role.roleType}</span>${role.name ? `<span class="aud-cast">「${role.name}」</span>` : ''}</span><span class="aud-stamp ${success ? 'ok' : 'no'}" id="audStamp">${success ? '试镜成功' : '试镜失败'}</span></div>`
            + `<div class="aud-rows">${rows.map(r =>
                `<div class="aud-row"><span class="aud-attr">${r.label}</span>`
                + `<span class="aud-vals">需 ≥ ${r.need}</span>`
                + `<span class="aud-cur">当前 <b class="${r.ok ? 'ok' : 'no'}">${r.cur}</b></span>`
                + `<span class="aud-mark ${r.ok ? 'ok' : 'no'}">${r.ok ? '✓' : '✗'}</span></div>`).join('')}</div>`
            + `<div class="aud-actions"><button class="pool-cta" id="audConfirm" disabled>确定</button></div>`
            + `</div>`;
        document.getElementById('audConfirm')!.addEventListener('click', e => {
            e.stopPropagation();
            this.closeAuditionCompare();
        });
        // 逐项自动连播，全部亮完 0.6s 在角色名右侧盖结论章+解锁确定键；
        // ⛔ 此处别调 renderShelf 刷角标——演出期间册行不可见，它只会顺带误亮周按钮（双按钮根源）；角标由返回池子页时的 renderShelf 重刷
        const rowEls = [...detail.querySelectorAll('.aud-row')] as HTMLElement[];
        rows.forEach((_, i) => {
            this.auditionTimers.push(window.setTimeout(() => rowEls[i]?.classList.add('on'), 350 + i * 500));
        });
        this.auditionTimers.push(window.setTimeout(() => {
            document.getElementById('audStamp')!.classList.add('show');
            (document.getElementById('audConfirm') as HTMLButtonElement).disabled = false;
        }, 350 + rows.length * 500 + 600));
    }

    /** 演出收尾：回详情页（按钮态反映判定结果；失败=锁至下个试镜周） */
    private closeAuditionCompare(): void {
        this.clearAuditionTimers();
        if (this.poolDetailIdx) {
            const s = GameData.SCRIPT_MAP[this.poolDetailIdx.scriptId];
            if (s) this.showPoolDetail(s, this.poolDetailIdx.roleIndex);
        }
    }

    private clearAuditionTimers(): void {
        this.auditionTimers.forEach(t => clearTimeout(t));
        this.auditionTimers = [];
    }

    // ===== 事件 VN 化（旁白态：无头像、无姓名；文案全用事件表原文） =====
    // 姓名栏留空是调用方的事：2026-10-05 令「随机事件不显示事件名」。
    // 10-14 组件侧 noAvatar 改成「只藏头像、姓名照 display」是为了让剧情里的路人（服装师等）有名无脸，
    // 事件标题不许跟着一起显形——两处 speaker 一律传 name 空串（剧情侧的旁白句同款）。

    private playEvent(): void {
        const w = this.current;
        if (!w || !w.event) return;
        const ev = w.event;
        const speaker: DialogSpeaker = { id: ev.id, name: '', noAvatar: true };
        this.vn.contentSlot.style.display = 'none';   // 事件演出：槽内容（册行/按钮）让位，读毕由 renderWeek 恢复
        const choices = eventSystem.getAvailableChoices(ev);
        // DialogChoice 只有 attrName/tag/prob 徽章；onPick 是 DialogLine（行级）回调——
        // 误放选项对象会被运行时静默忽略 ⇒ 结算不执行、eventPending 永不清（2026-10-05 实测死循环根因）
        const vnChoices: import('../VnDialog').DialogChoice[] = choices.map(choice => {
            const prob = choice.type === 'check' ? eventSystem.getSuccessProbability(ev, choice) : null;
            return {
                text: choice.text,
                attrName: choice.type === 'check' ? attrName(choice.bindAttr) : undefined,
                tag: choice.type === 'tag' ? (GameData.TAG_MAP[choice.requiredTag]?.name ?? undefined) : undefined,
                prob: prob ?? undefined,
                inPlace: choice.type === 'check',   // 检定型：选中行原位掷骰（D-05，原版同款）
            };
        });
        // 事件顶掉当前卡：试镜周从「完成试镜」键直接进事件时，周旁白的收口还挂着，
        // 不顶掉它日后会再演一次同一事件
        this.vn.playNow(
            [{
                speaker,
                text: ev.description,
                choices: vnChoices,
                onPick: (idx, btn) => this.pickChoice(ev, choices[idx], btn),
            }],
            () => this.onEventEnd(),
        );
    }

    /** 玩家选择 → 执行判定 → 检定型在选中行内原位掷骰（原版 D-05 同款）→ 定格停留 → 结算句（打字机）+变动行挂句下（打完浮现） */
    private pickChoice(event: EventDef, choice: ChoiceDef, btn?: HTMLButtonElement): void {
        const res = eventSystem.executeChoice(event, choice);
        const main = choice.type === 'check' ? (res.success ? choice.successText : choice.failText) : choice.resultText;
        // 属性/资源变动：⛔ 括号纯文本，改行程文案同款变动行（整行显示）；组内属性在前、资源在后（行程变动行同规）
        const isRes = (label: string) => label === '体力' || label === '金钱' || label === '名气';
        const changes = Object.entries(choice.type === 'check' && !res.success ? (choice.failEffects ?? {}) : choice.effects)
            .filter(([, v]) => v !== 0)
            .map(([k, v]) => ({ label: effectLabel(k), delta: `${v > 0 ? '+' : ''}${v}` }))
            .sort((a, b) => Number(isRes(a.label)) - Number(isRes(b.label)));   // 稳定排序：组内保持定义序
        const speaker: DialogSpeaker = { id: event.id, name: '', noAvatar: true };   // 姓名栏同上留空（10-05 令）
        // 结果句（打字机）+ 变动行挂在句下（打完后浮现，同框 ⛔ 换页）
        const playResult = () => {
            const lines: DialogLine[] = [{ speaker, text: main, changes }];
            this.vn.play(lines, () => this.onEventEnd());
        };
        // 原版 D-05（38 批）同款：选中行的概率徽章原位替换为「骰子+点数」翻滚，定格瞬间成败显形+整行染色
        // （渲染帧就挂 check-ok/check-ng 会提前剧透判定——2026-10-01 用户拍板「保留悬念」）
        if (choice.type === 'check' && res.roll > 0 && btn) {
            const meta = btn.querySelector('.opt-meta');
            if (!meta) { playResult(); return; }
            // 登记演出链上全部定时器：面板销毁（退出游戏）落在动效窗口内时整链停掉，
            // ⛔ 让 revealVerdict→playResult→vn.play 在已摘除的 DOM 上复活
            const timers: number[] = [];
            this.diceCancel = () => {
                timers.forEach(t => { window.clearInterval(t); window.clearTimeout(t); });
                this.diceCancel = null;
            };
            meta.querySelector('.opt-chip.prob')?.remove();
            const chip = createSpan('opt-chip prob roll');
            const cube = createDiceCube(res.roll);
            chip.appendChild(cube);
            const rollVal = createSpan('roll-val', '6点');
            chip.appendChild(rollVal);
            meta.appendChild(chip);
            const verdict = createSpan('opt-chip verdict', '');
            meta.appendChild(verdict);
            const revealVerdict = () => {
                btn.classList.add(res.success ? 'check-ok' : 'check-ng');
                verdict.textContent = res.success ? '✓ 通过' : '✗ 未通过';
                // 定格后停留一会让玩家看清结果（2026-10-05 用户令），再进结算句
                timers.push(window.setTimeout(playResult, 900));
            };
            if (Utils.prefersReducedMotion()) {
                rollVal.textContent = `${res.roll}点`;
                revealVerdict();
                return;
            }
            // 先同帧瞬摆回 1 点面朝前（无过渡），下一帧起 .45s 过渡落向目标面（垫 720° 整圈=视觉翻滚、落定朝向不变）
            const [lx, ly] = DICE_LAND[res.roll] ?? [0, 0];
            cube.style.transition = 'none';
            cube.style.transform = 'perspective(8em) rotateX(0deg) rotateY(0deg)';
            requestAnimationFrame(() => requestAnimationFrame(() => {
                cube.style.transition = 'transform .45s cubic-bezier(.18, .65, .32, 1)';
                cube.style.transform = `perspective(8em) rotateX(${720 + lx}deg) rotateY(${720 + ly}deg)`;
            }));
            let t = 0;
            const tick = window.setInterval(() => {
                t++;
                rollVal.textContent = `${Math.max(1, 7 - t)}点`;
                if (t >= 5) {
                    window.clearInterval(tick);
                    rollVal.textContent = `${res.roll}点`;
                    revealVerdict();
                }
            }, 90);
            timers.push(tick);
            return;
        }
        playResult();
    }

    // ===== 试镜周判定 =====

    private isAuditionWeek(w: WeekResult): boolean {
        if (this.auditionWeek) return true;
        if (w.forcedRest) return false;
        const state = GameState.getInstance();
        const idx = w.week - 1;
        const slot: ScheduleSlot | null = state.player.schedule[idx] ?? null;
        return !!slot && slot.kind === 'audition';
    }

    // ===== 片场偶遇（熟遇四格 2026-10-10 改版：闲聊/送礼/邀约/离开，选完即收场） =====

    private maybePlayEncounter(): void {
        const enc = this.current?.encounter;
        // 不许加 `vn.open` 守卫（2026-10-10 修）：调用点是旁白读毕出口，而月度叙出走 VN 常驻模式＝段尾不收卡，
        // vn.open 恒真，旧守卫把偶遇拦成永不演出。事件那条路（playEvent）本就直接 play 覆盖叙事卡，偶遇同形制。
        if (!enc || this.encounterResult) return;
        // 咖位差「够不着」（2026-10-12）：纯旁白 + 变动行「咖位还不足以结识此人」（2026-10-13 茄子令）、无选项、不结识
        if (enc.unreachable) {
            this.vn.play(
                enc.lines.map(text => ({ speaker: NARRATOR, text, changes: [{ label: '', delta: UNREACHABLE_LINES.hint }] })),
                () => this.afterEncounter(),
            );
            return;
        }
        const speaker: DialogSpeaker = { id: enc.npcId, name: enc.npcName, avatarChar: enc.npcName.slice(0, 1) };
        const build = () => enc.lines.map((text, i) => {
            if (i < enc.lines.length - 1) return { speaker, text };
            return {
                speaker,
                text,
                // reply 为空的格子（离开＝不回应；邀约＝反应句要等结算掷点）不挂连播句
                choices: enc.choices
                    .map(c => ({
                        text: c.text,
                        ...(typeof c.prob === 'number' ? { prob: c.prob } : {}),
                        ...(c.reply ? { reply: { speaker, text: c.reply, ...(c.changes ? { changes: c.changes } : {}) } } : {}),
                    })),
                onPick: (idx: number) => this.resolveEncounter(enc, idx, speaker),
            };
        });
        const play = () => {
            if (this.encounterResult || this.current?.encounter !== enc) return;
            // 2026-10-16 首拍周保底撤销，这里原先那句「演出交到玩家眼前才盖章」（claim）随之删掉——
            // 掷点方与本方都不再记「这部戏遇过几次」的账，读毕只负责把周收口。
            // 读完（含选项反应句）走垫底出口（剧情在偶遇之后弹，2026-10-24 茄子令）：
            // 不要回 renderWeek，否则同一段周叙事要读第二遍
            this.vn.play(build() as DialogLine[], () => this.afterEncounter());
        };
        withSpeakerAvatar(speaker, play);
    }

    /**
     * 玩家选定 → 结算。
     * 闲聊/送礼/离开：反应句已挂在选项上（由 VnDialog 原位连播），这里只记账，推进交给 play 的 onEnd（=进下周）。
     * 邀约：成败要在此刻才掷，反应句与「好感 +X」变动由 resolve 现出、这里现场续播一段 VN
     *  （走 VnDialog 的 replayed 通道：选项⛔ 挂 reply，onPick 内自播即不被 hide 吞掉）。
     */
    private resolveEncounter(enc: EncounterOffer, idx: number, speaker: DialogSpeaker): void {
        const outcome = encounterSystem.resolve(enc, idx);
        this.encounterResult = outcome;
        if (outcome.reply) {
            this.vn.play([{ speaker, text: outcome.reply, ...(outcome.changes ? { changes: outcome.changes } : {}) } as DialogLine],
                () => this.afterEncounter());
        }
    }

    // ===== 结算与 5c 竞角揭晓（经纪人聊天页，原样保留） =====

    /** 结算并返回决策日 */
    private finish(): void {
        // 收月：所有「未成形」（week 未定）的挂起之约作废（多槽批 2026-10-29：逐条清，已定周的保留——
        // 约没成形，月限不退；⛔ 不当场落盘，月结存档（GameLoop.doFinishMonth）随后捎带）
        const list = GameState.getInstance().player.pendingInvites;
        if (list) GameState.getInstance().player.pendingInvites = list.filter(i => i.week);
        // 月末有待揭晓的竞角结果：掷点建队列，并把队列行挂进月末结转包（跨这次存盘）。
        // 2026-11-03 竞角重排批：月结（换月 + 月初掷点 + 存盘）挪到队列**之前**；
        // 玩家在第一个弹窗退出时，读档落在「下月月初」那一格；队列与掷出的成败跟着存档走，原样再弹、不重掷。
        // 收口夜的当月与剧单由月结的甲段（GameState.sweepClosingScripts）补进结转包。
        if (scriptSystem.hasPendingReveals()) {
            this.revealResults = this.sortRevealQueue(scriptSystem.revealAuditions());
            this.revealHandled.clear();
            this.revealQueueIdx = 0;
            GameState.getInstance().openHandover(
                this.revealResults.map(r => ({ scriptId: r.scriptId, roleIndex: r.roleIndex })));
        }
        this.hideWeekPop();
        gameLoop.finishMonth();      // 触发 MONTH_END → 月结算 + 进入决策日（可能触发 GAME_OVER）
        if (this.app.currentPanel !== 'gameOver') {
            this.app.show('decision');   // 背景=决策页；队列由入场链 ④ 消费，弹完跑收口夜乙段
        }
    }

    private roleLabelForScript(script: ScriptDef, roleIndex: number): string {
        const role = script.roles[roleIndex];
        if (!role) return '';
        return roleLabel(role);
    }

    /** 某条竞角结果的全套读法（列表行与弹窗共用一份，⛔ 两处各判一遍成败/占位） */
    private revealState(r: import('../../systems/ScriptSystem').RevealResult): {
        itemKey: string; handled: boolean; isContracted: boolean; siblingSigned: boolean;
        declined: boolean; otherSigned: boolean; status: { text: string; ok: boolean; mute?: boolean };
    } {
        const state = GameState.getInstance();
        const itemKey = `${r.scriptId}_${r.roleIndex}`;
        const handled = this.revealHandled.has(itemKey);
        const isContracted = state.player.contracts.some(
            c => c.scriptId === r.scriptId && c.roleIndex === r.roleIndex);
        const siblingSigned = state.player.contracts.some(
            c => c.scriptId === r.scriptId && c.status === 'active' && c.roleIndex !== r.roleIndex);
        // 徽章优先级：已签＝「已签约」；处理过＝「已放弃」（红，行随 st-no）；竞角失败＝「竞角失败」
        // （红）；竞角成功但他角已签＝「已签他角」（灰，原纸底角标 .reveal-othersigned 下线，胶囊
        // 就地承担）；否则「竞角成功」。⛔ 再叠同义角标/文案行——徽章承担
        const status = isContracted ? { text: '已签约', ok: true }
            : handled ? { text: '已放弃', ok: false }
                : !r.success ? { text: '竞角失败', ok: false }
                    : siblingSigned ? { text: '已签他角', ok: false, mute: true }
                        : { text: '竞角成功', ok: true };
        return {
            itemKey, handled, isContracted, siblingSigned,
            declined: handled && !isContracted,
            otherSigned: siblingSigned && !isContracted && !handled && r.success,
            status,
        };
    }

    /** app 注入顶层宿主（决策页为背景时弹窗必须挂在 shell 级，⛔ 塞进 hidden-panel 的面板容器） */
    attachRevealHost(host: HTMLElement): void {
        this.revealLight.attachTo(host);
    }

    /** 决策页入场链 ④ 入口：有未消费的快照才接管（返回 true=链押给 5c 队列） */
    consumeRevealIfPending(): boolean {
        if (!this.revealResults.length) this.restoreRevealQueue();
        if (!this.revealResults.length || this.revealQueueIdx >= this.revealResults.length) return false;
        this.showNextReveal();
        return true;
    }

    /**
     * 从月末结转包恢复竞角队列（2026-11-03 竞角重排批，读档后接着弹）：
     * 掷出的成败记在 `auditionReveal` 里、跟着存档走，原样再弹、不重掷。
     * 呈现序＝存进结转包时的序（存之前已按角色等级排过，同剧相邻）。
     */
    private restoreRevealQueue(): void {
        const state = GameState.getInstance();
        const rows = state.player.monthHandover?.rows ?? [];
        if (!rows.length) return;
        this.revealResults = rows.map(r => ({
            scriptId: r.scriptId,
            roleIndex: r.roleIndex,
            success: state.player.auditionReveal?.[Utils.auditionRevealKey(r.scriptId, r.roleIndex)] === true,
        }));
        this.revealHandled.clear();
        this.revealQueueIdx = 0;
    }

    /**
     * 打开竞角结果弹窗（2026-10-30 签约呈现改造批：成败同层、轻档戏票）：
     * 剧名 → 角色名 → 番位＋竞角状态胶囊 → 周期/片酬，底部两颗决策键（放弃 ghost＋签约/带资进组）。
     * 强制二选一（点遮罩⛔ 关闭、无 X、无返回键）；操作成功即关层，背后列表角标随之刷新。
     */
    private openRevealDetail(scriptId: string, roleIndex: number, onResolved?: () => void): void {
        const script = GameData.SCRIPT_MAP[scriptId];
        const result = this.revealResults.find(r => r.scriptId === scriptId && r.roleIndex === roleIndex);
        const role = script?.roles[roleIndex];
        if (!script || !result || !role) return;
        const state = GameState.getInstance();
        const s = this.revealState(result);

        const active = state.player.contracts.filter(c => c.status === 'active').length;
        const slotFull = active >= GameConfig.MAX_PENDING_SCRIPTS;
        // 一剧一角色的 UI 侧判据（2026-10-25 直签批扩 pending）：挂着待签直签 offer 的剧，
        // 试镜签约同样被系统层挡——这里同步置灰并说原因，⛔ 让玩家点了才被告知
        const scriptAlreadyContracted = state.player.contracts.some(
            c => c.scriptId === scriptId && (c.status === 'active' || c.status === 'pending'));
        const gateHint = slotFull || scriptAlreadyContracted
            ? this.blockedReason(script, roleIndex, slotFull, scriptAlreadyContracted)
            : '';
        const price = Math.floor(scriptSystem.rewardOf(role) * GameConfig.BUYIN_MULTIPLIER);
        const buyinUnlocked = state.hasFlag(GameConfig.BUYIN_UNLOCK_FLAG);

        const buttons: SignPopButton[] = [];
        // 放弃：本周期内标记不签（会话级 revealHandled，不写存档）；已签约/已放弃后置灰
        buttons.push({
            label: s.declined ? '已放弃' : '放弃',
            ghost: true,
            disabled: s.isContracted || s.handled,
            run: () => {
                // 放弃＝这一位表态完了：资格/记忆/队列行一起收掉（与签约、带资同一条结算）
                scriptSystem.settleAudition(scriptId, roleIndex);
                this.revealHandled.add(s.itemKey);
                // 后果由按钮变「已放弃」+ 置灰表达，不再弹提示
            },
        });

        if (result.success) {
            const primary: SignPopButton = { label: '签约' };
            if (s.isContracted) Object.assign(primary, { label: '已签约', disabled: true, done: true });
            else if (s.handled) Object.assign(primary, { disabled: true });
            else if (gateHint) {
                Object.assign(primary, {
                    blocked: true,
                    run: () => { showHint(gateHint); return false; },   // 置灰可点：点了说原因，⛔ 静默
                });
            } else {
                primary.run = () => {
                    // 竞角成败的判据在系统层（`ScriptSystem.contractScript`），这里只认它的返回值：
                    // 拒签就不许把这一位结算掉（`settleAudition` 一调，资格与记忆一起作废＝玩家白白丢一个位），
                    // 也不许静默——点了没反应＝玩家以为按钮坏了。
                    if (!scriptSystem.contractScript(scriptId, roleIndex)) {
                        showHint('签约没走成：这一位的竞角结果不是「竞得」。');
                        return false;
                    }
                    scriptSystem.settleAudition(scriptId, roleIndex);
                    this.revealHandled.add(s.itemKey);
                    // 签约成功由按钮变「已签约」+ 置灰表达，不再弹提示
                };
            }
            buttons.push(primary);
        } else {
            const primary: SignPopButton = {
                label: buyinUnlocked ? `带资进组 ¥${Utils.formatMoney(price)}` : '带资进组',
            };
            if (s.isContracted) Object.assign(primary, { label: '已签约', disabled: true, done: true });
            else if (s.handled) Object.assign(primary, { disabled: true });
            else if (!buyinUnlocked) {
                Object.assign(primary, {
                    disabled: true,
                    title: `未解锁：金钱首次达到 ¥${Utils.formatMoney(GameConfig.BUYIN_UNLOCK_MONEY)} 时由经纪人解锁`,
                });
            } else if (gateHint) {
                Object.assign(primary, {
                    blocked: true,
                    run: () => { showHint(gateHint); return false; },
                });
            } else if (state.getMoney() < price) {
                Object.assign(primary, { disabled: true, title: '金钱不足' });
            } else {
                primary.run = () => {
                    scriptSystem.buyIn(scriptId, roleIndex);
                    scriptSystem.settleAudition(scriptId, roleIndex);
                    this.revealHandled.add(s.itemKey);
                    // 带资成功由按钮变「已签约」+ 金钱读数变化表达，不再弹提示
                };
            }
            buttons.push(primary);
        }

        this.revealLight.open({
            tier: 'light',
            info: {
                script: script.name,
                name: role.name || role.roleType,
                pos: role.name ? role.roleType : '',
                status: s.status,
                stats: [
                    { label: '周期', value: `${role.shootWeeks} 周` },
                    { label: '片酬', value: `¥${Utils.formatMoney(scriptSystem.rewardOf(role))}` },
                ],
            },
            buttons,
            onResolved: () => onResolved?.(),
        });
    }

    private blockedReason(script: ScriptDef, roleIndex: number, slotFull: boolean, scriptAlreadyContracted: boolean): string {
        if (scriptAlreadyContracted) {
            const state = GameState.getInstance();
            // 一剧一角色的「已占」两种态：直签待签 offer（回决策日让经纪人递到面前）或已在拍的其他角色
            const pending = state.player.contracts.find(
                x => x.scriptId === script.id && x.status === 'pending' && x.roleIndex !== roleIndex
            );
            if (pending) return '这部剧有直签戏约还挂着——先回决策日把它签了或回绝了';
            const c = state.player.contracts.find(
                x => x.scriptId === script.id && x.status === 'active' && x.roleIndex !== roleIndex
            );
            const label = c ? this.roleLabelForScript(script, c.roleIndex) : '其他角色';
            return `已签约本剧「${label}」，一部剧只能签一个角色`;
        }
        return `通告名额已满（上限 ${GameConfig.MAX_PENDING_SCRIPTS} 个），可在经纪人处解约腾出名额`;
    }

    /** 定角夜=队列逐个直弹（2026-10-30 茄子实测令：聊天列表页退役——胜出/失败位依次弹轻档签约弹窗，全弹完进月结） */
    private revealQueueIdx = 0;

    /**
     * 队列排序（2026-11-02 茄子令）：整队按角色等级降序，且同一部剧的两个位必须相邻——
     * 低位紧跟着同剧高位之后弹。先按「组内最高等级」降序排组（Array.sort 稳定，同分维持入册序），
     * 再在组内按等级降序。两条要求打架时相邻压过全局降序：本剧低位可能排在别剧高位之前弹。
     * 呈现序：随月末结转包一起存盘（2026-11-03 竞角重排批，读档后原样再弹，顺序不变），
     * 不动系统层那份名单（资格与成败记忆在 ScriptSystem / player.auditionReveal）。
     */
    private sortRevealQueue(results: import('../../systems/ScriptSystem').RevealResult[]):
        import('../../systems/ScriptSystem').RevealResult[] {
        const levelOf = (r: import('../../systems/ScriptSystem').RevealResult) =>
            GameData.SCRIPT_MAP[r.scriptId]?.roles[r.roleIndex]?.level ?? 0;
        const groups = new Map<string, import('../../systems/ScriptSystem').RevealResult[]>();
        for (const r of results) {
            const g = groups.get(r.scriptId);
            if (g) g.push(r);
            else groups.set(r.scriptId, [r]);
        }
        return [...groups.values()]
            .map(g => g.sort((a, b) => levelOf(b) - levelOf(a)))
            .sort((a, b) => levelOf(b[0]) - levelOf(a[0]))
            .reduce((acc, g) => acc.concat(g), [] as import('../../systems/ScriptSystem').RevealResult[]);
    }

    /** 队列取下一位弹轻档；取空=定角夜结束 → onRevealDone（月结，决策页已在屏） */
    private showNextReveal(): void {
        while (this.revealQueueIdx < this.revealResults.length) {
            const r = this.revealResults[this.revealQueueIdx++];
            if (this.revealHandled.has(`${r.scriptId}_${r.roleIndex}`)) continue;
            this.openRevealDetail(r.scriptId, r.roleIndex, () => this.showNextReveal());
            return;
        }
        this.onRevealDone?.();
    }
}
